import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // every 5 minutes

export const usePassExpirationCheck = () => {
  const { toast } = useToast();
  const running = useRef(false);

  useEffect(() => {
    const check = async () => {
      if (running.current) return;
      running.current = true;

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Find clients with expired passes (pass_end in the past, fields still set)
        const { data: expired, error } = await supabase
          .from('clients')
          .select('id, first_name, last_name, case_worker_id, site_id, status')
          .not('pass_end', 'is', null)
          .not('pass_start', 'is', null)
          .lt('pass_end', new Date().toISOString())
          .is('archived_at', null);

        if (error || !expired?.length) return;

        for (const client of expired) {
          const clientName = `${client.first_name} ${client.last_name}`.trim();
          const alertTitle = `Pass Expired: ${clientName}`;
          const nowIso = new Date().toISOString();

          // Dedup: check for existing active alert
          const { data: existing } = await supabase
            .from('alerts')
            .select('id')
            .eq('client_id', client.id)
            .eq('title', alertTitle)
            .eq('status', 'active')
            .limit(1);

          if (existing && existing.length > 0) {
            // Already alerted — keep the pass fields/status intact so staff can edit, clear, or manually unbed.
            continue;
          }

          // Create alert
          const { data: alert } = await supabase
            .from('alerts')
            .insert({
              type: 'warning',
              category: 'staff',
              title: alertTitle,
              description: `${clientName}'s pass has expired. Bed remains held pending staff review. Check the client back in, edit/extend the pass, clear the pass, or manually unbed if appropriate.`,
              client_id: client.id,
              site_id: client.site_id,
              created_by: user.id,
              status: 'active',
            })
            .select('id')
            .single();

          // Gather notification recipients
          const recipientIds = new Set<string>();

          // Case manager
          if (client.case_worker_id) {
            recipientIds.add(client.case_worker_id);
          }

          // All super admins
          const { data: admins } = await supabase
            .from('user_roles')
            .select('user_id')
            .eq('role', 'super_admin');
          admins?.forEach((r) => recipientIds.add(r.user_id));

          // Site staff
          if (client.site_id) {
            const { data: siteUsers } = await supabase
              .from('user_sites' as any)
              .select('user_id')
              .eq('site_id', client.site_id);
            (siteUsers as any[])?.forEach((r: any) => recipientIds.add(r.user_id));
          }

          // Insert notifications
          const notifications = Array.from(recipientIds).map((uid) => ({
            user_id: uid,
            type: 'pass_expired',
            title: alertTitle,
            message: `${clientName}'s pass expired. Bed held pending staff review.`,
            alert_id: alert?.id || null,
          }));

          if (notifications.length > 0) {
            await supabase.from('notifications').insert(notifications);
          }

          // History timeline entry
          if (client.site_id) {
            await supabase.from('service_logs').insert({
              client_id: client.id,
              site_id: client.site_id,
              service_type: 'status_change',
              quantity: 1,
              notes: `Pass expired — alert generated. Bed held pending staff decision.`,
              provided_by: user.id,
            });
          }

          toast({
            title: 'Pass Expired Alert',
            description: `${clientName}'s pass expired. Bed held pending staff review.`,
            variant: 'destructive',
          });
        }
      } catch (err) {
        console.error('Pass expiration check error:', err);
      } finally {
        running.current = false;
      }
    };

    // Run immediately on mount, then on interval
    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [toast]);
};
